import type {
  BoothCapturesResponse,
  BoothDeleteExecuteRequest,
  BoothDeleteExecuteResponse,
  BoothDeleteRequestRequest,
  BoothDeleteRequestResponse,
  BoothManifest,
  CreateBoothCaptureRequest,
  CreateBoothCaptureResponse,
  CreateBoothRequest,
  CreateBoothResponse,
  CreateUploadRequest,
  CreateUploadResponse,
  ErrorResponse,
} from '@photobooth/shared';
import { logger } from '../utils/log';

const log = logger('api');

function apiBase(): string {
  const v = import.meta.env.VITE_API_BASE_URL;
  if (!v) {
    throw new Error('VITE_API_BASE_URL is not configured (.env.example)');
  }
  return v.replace(/\/+$/, '');
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly body: ErrorResponse | null;

  constructor(message: string, status: number, body: ErrorResponse | null) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function isTransient(status: number): boolean {
  return status === 408 || status === 425 || status === 429 || status >= 500;
}

async function withRetry<T>(
  fn: () => Promise<T>,
  attempts = 3,
  baseDelayMs = 400,
): Promise<T> {
  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      const status = err instanceof ApiError ? err.status : 0;
      const transient = status === 0 || isTransient(status);
      if (!transient || i === attempts - 1) throw err;
      const jitter = Math.random() * 100;
      await sleep(baseDelayMs * 2 ** i + jitter);
    }
  }
  throw lastErr;
}

export async function createUpload(
  req: CreateUploadRequest,
  options: { fetch?: typeof fetch } = {},
): Promise<CreateUploadResponse> {
  const f = options.fetch ?? fetch;
  log.info('createUpload', req);
  return withRetry(async () => {
    const res = await f(`${apiBase()}/uploads`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(req),
    });
    if (!res.ok) {
      let body: ErrorResponse | null = null;
      try {
        body = (await res.json()) as ErrorResponse;
      } catch {
        /* non-JSON */
      }
      throw new ApiError(
        body?.message ?? `createUpload failed (${res.status})`,
        res.status,
        body,
      );
    }
    const body = (await res.json()) as CreateUploadResponse;
    log.info('createUpload response', { shareId: body.shareId, shareUrl: body.shareUrl });
    return body;
  });
}

export async function uploadBlob(
  uploadUrl: string,
  blob: Blob,
  contentType: string,
  options: { fetch?: typeof fetch } = {},
): Promise<void> {
  const f = options.fetch ?? fetch;
  log.info('uploadBlob', { size: blob.size, contentType });
  const t0 = performance.now();
  await withRetry(async () => {
    const res = await f(uploadUrl, {
      method: 'PUT',
      headers: { 'content-type': contentType },
      body: blob,
    });
    if (!res.ok) {
      log.error('uploadBlob failed', res.status, res.statusText);
      throw new ApiError(
        `uploadBlob failed (${res.status})`,
        res.status,
        null,
      );
    }
  });
  log.info('uploadBlob complete', `${(performance.now() - t0).toFixed(0)}ms`);
}

// ===========================================================================
// Custom booth API
// ===========================================================================

export async function createBooth(
  req: CreateBoothRequest,
): Promise<CreateBoothResponse> {
  log.info('createBooth', { slug: req.slug, modes: req.modes.length, effects: req.effects.length });
  return withRetry(async () => {
    const res = await fetch(`${apiBase()}/api/booths`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(req),
    });
    if (!res.ok) {
      let body: ErrorResponse | null = null;
      try {
        body = (await res.json()) as ErrorResponse;
      } catch {
        /* non-JSON */
      }
      throw new ApiError(
        body?.message ?? `createBooth failed (${res.status})`,
        res.status,
        body,
      );
    }
    return (await res.json()) as CreateBoothResponse;
  });
}

export async function createBoothCapture(
  slug: string,
  req: CreateBoothCaptureRequest,
): Promise<CreateBoothCaptureResponse> {
  return withRetry(async () => {
    const res = await fetch(
      `${apiBase()}/api/booths/${encodeURIComponent(slug)}/captures`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(req),
      },
    );
    if (!res.ok) {
      let body: ErrorResponse | null = null;
      try {
        body = (await res.json()) as ErrorResponse;
      } catch {
        /* non-JSON */
      }
      throw new ApiError(
        body?.message ?? `createBoothCapture failed (${res.status})`,
        res.status,
        body,
      );
    }
    return (await res.json()) as CreateBoothCaptureResponse;
  });
}

export async function fetchBoothCaptures(slug: string): Promise<BoothCapturesResponse> {
  const res = await fetch(
    `${apiBase()}/api/booths/${encodeURIComponent(slug)}/captures`,
    { cache: 'no-store' },
  );
  if (!res.ok) {
    let body: ErrorResponse | null = null;
    try {
      body = (await res.json()) as ErrorResponse;
    } catch {
      /* non-JSON */
    }
    throw new ApiError(
      body?.message ?? `fetchBoothCaptures failed (${res.status})`,
      res.status,
      body,
    );
  }
  return (await res.json()) as BoothCapturesResponse;
}

export async function requestBoothDelete(
  slug: string,
  req: BoothDeleteRequestRequest,
): Promise<BoothDeleteRequestResponse> {
  const res = await fetch(
    `${apiBase()}/api/booths/${encodeURIComponent(slug)}/delete/request`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(req),
    },
  );
  if (!res.ok) {
    let body: ErrorResponse | null = null;
    try {
      body = (await res.json()) as ErrorResponse;
    } catch {
      /* non-JSON */
    }
    throw new ApiError(
      body?.message ?? `requestBoothDelete failed (${res.status})`,
      res.status,
      body,
    );
  }
  return (await res.json()) as BoothDeleteRequestResponse;
}

export async function executeBoothDelete(
  slug: string,
  req: BoothDeleteExecuteRequest,
): Promise<BoothDeleteExecuteResponse> {
  const res = await fetch(
    `${apiBase()}/api/booths/${encodeURIComponent(slug)}/delete/execute`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(req),
    },
  );
  if (!res.ok) {
    let body: ErrorResponse | null = null;
    try {
      body = (await res.json()) as ErrorResponse;
    } catch {
      /* non-JSON */
    }
    throw new ApiError(
      body?.message ?? `executeBoothDelete failed (${res.status})`,
      res.status,
      body,
    );
  }
  return (await res.json()) as BoothDeleteExecuteResponse;
}

/** Fetch the manifest for a custom booth. Reads directly from S3 via CloudFront. */
export async function fetchBoothManifest(slug: string): Promise<BoothManifest | null> {
  // The manifest lives at PUBLIC_MEDIA_BASE_URL/booths/{slug}/manifest.json.
  // We don't know that URL at runtime from the SPA — but our CloudFront
  // setup routes the SAME origin (photobooth.ofunc.io) to S3 by default,
  // so a same-origin fetch of /booths/{slug}/manifest.json reaches it.
  const url = `/booths/${encodeURIComponent(slug)}/manifest.json`;
  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`manifest fetch failed (${res.status})`);
    return (await res.json()) as BoothManifest;
  } catch (err) {
    log.warn('fetchBoothManifest', slug, err);
    return null;
  }
}
